import * as Yup from 'yup';
/* eslint-disable import/no-extraneous-dependencies */
import { t } from 'src/lib/language';
import { PAYMENT_STATUS, PAYMENT_TYPE } from 'src/models/PaymentModel';
import moment from 'moment';
import { idFieldRequired, moneyField, onlinePaymentIdField } from 'src/lib/ValidatorHelper';

const isOnlinePayment = (
  type: PAYMENT_TYPE,
) => [PAYMENT_TYPE.cc, PAYMENT_TYPE.eft].includes(type);
const isOfflinePayment = (
  type: PAYMENT_TYPE,
) => ![PAYMENT_TYPE.cc, PAYMENT_TYPE.eft].includes(type);

const AdminDonationValidator = (lang:string) => Yup.object().shape({
  paid: moneyField()
    .required(),
  quantity: Yup.number()
    .positive()
    .required(),
  contact_id: idFieldRequired()
    .required(),
  bank_account_id: Yup.number()
    .nullable() // ← ADD THIS BASELINE
    .when(['is_promised_donation', 'payment_type'], {
      is: (isPromised: boolean, type: PAYMENT_TYPE) => {
        if (isPromised) return false;
        return isOnlinePayment(type);
      },
      then: (schema) => schema.required().min(1, t(lang, 'validations.invoice.field_required')).integer(),
      otherwise: (schema) => schema.notRequired(), // ← Better than just nullable()
    }),
  product_id: Yup.number()
    .min(1, t(lang, 'validations.invoice.field_required'))
    .integer()
    .required(),
  payment_gateway_id: Yup.number()
    .nullable()
    .when(['is_promised_donation', 'payment_type'], {
      is: (isPromised: boolean, type: PAYMENT_TYPE) => {
        if (isPromised) return false; // Priority 1: optional
        return isOnlinePayment(type); // Priority 2: required for online
      },
      then: (schema) => schema.required().min(1, t(lang, 'validations.invoice.field_required')).integer(),
      otherwise: (schema) => schema.notRequired(),
    }),
  seller_id: Yup.number()
    .integer(),
  scheduled_date: Yup.date()
    .nullable()
    .when(['is_promised_donation', 'payment_status', 'payment_type'], {
      is: (isPromised: boolean, status: PAYMENT_STATUS, type: PAYMENT_TYPE) => {
        if (isPromised) return false;
        return (isOfflinePayment(type) && status === PAYMENT_STATUS.scheduled)
          || isOnlinePayment(type);
      },
      then: (schema) => schema.min(moment().startOf('day')).required(),
      otherwise: (schema) => schema.nullable(),
    }),
  paid_date: Yup.date()
    .when(['payment_type', 'payment_status'], {
      is: (
        type: PAYMENT_TYPE,
        status: PAYMENT_STATUS,
      ) => isOfflinePayment(type) && status === PAYMENT_STATUS.paid,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
  start_date: Yup.date()
    .when(['is_promised_donation', 'is_installment'], {
      is: (isPromised: boolean, isInstallment: boolean) => {
        if (isPromised) return false;
        return isInstallment;
      },
      then: (schema) => schema.min(moment().startOf('day')).required(),
      otherwise: (schema) => schema.nullable(),
    }),
  processed_date: Yup.date()
    .nullable(),
  payment_info_id: Yup.number()
    .when('is_promised_donation', {
      is: true,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: () => onlinePaymentIdField(lang),
    }),
  // Event
  is_anonymous: Yup.boolean(),
  event_id: Yup.number()
    .when('is_linked', {
      is: (value: boolean) => value,
      then: (schema) => schema.integer().min(1).required(),
      otherwise: (schema) => schema.nullable(),
    }),
  ambassador_id: Yup.number()
    .when('is_linked', {
      is: (value: boolean) => value,
      then: (schema) => schema.integer().min(1).nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
  donor_name_displayed: Yup.string()
    .when('is_linked', {
      is: (value: boolean) => value,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
  amount_multiplier: Yup.number()
    .when('is_linked', {
      is: (value: boolean) => value,
      then: (schema) => schema.min(0.01).nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
  donor_comment: Yup.string().nullable(),
  source_reference: Yup.string().nullable(),
  source_reference_url: Yup.string().nullable(),
});

export default AdminDonationValidator;
